import { Option } from '@interfaces/common/option';
import { DerivativesHttpService } from '@src/app/core/services/http/derivatives/derivatives-http.service';
import { Observable, forkJoin, map } from 'rxjs';

/** Derivativo vinculado ao comitê novo: id persistido e o código visual (`DERIV3818` ou só o número). */
export interface CommitteeDerivativeRef {
  id: string;
  sequentialId: string;
}

/**
 * Lista de derivativos do prefill (card 3108). `derivatives` vem da seleção múltipla da tela de
 * Derivativos; `derivativeOperationId` é o contrato antigo de um derivativo só (menus ⋯, tela NDF).
 * Sem vazios e sem repetidos, na ordem recebida.
 */
export function normalizePrefillDerivatives(prefill: {
  derivatives?: CommitteeDerivativeRef[];
  derivativeOperationId?: string;
  derivativeSequentialId?: string;
}): CommitteeDerivativeRef[] {
  const source = prefill.derivatives?.length
    ? prefill.derivatives
    : prefill.derivativeOperationId
      ? [{ id: prefill.derivativeOperationId, sequentialId: prefill.derivativeSequentialId ?? '' }]
      : [];

  const seen = new Set<string>();
  return source.filter(item => {
    const key = item.id?.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Rótulo de um derivativo: o código do backend (`DERIV3818`) como veio; número solto vira `#3818`. */
export function formatDerivativeRef(
  sequentialId: string | null | undefined,
  fallbackId = ''
): string {
  const value = (sequentialId ?? '').trim();
  if (!value) return fallbackId ? `#${fallbackId}` : '-';
  return /^deriv/i.test(value) ? value.toUpperCase() : `#${value}`;
}

/**
 * Opções da multisseleção de derivativos: os disponíveis da demanda mais os já vinculados (para
 * nunca sumir um marcado). Sem disponíveis, devolve lista vazia e o campo fica travado.
 */
export function buildDerivativeChoices(
  available: CommitteeDerivativeRef[],
  linked: CommitteeDerivativeRef[]
): { refs: CommitteeDerivativeRef[]; options: Option[] } {
  const refs = normalizePrefillDerivatives({ derivatives: [...linked, ...available] });
  const options =
    available.length > 0
      ? refs.map(item => ({
          label: formatDerivativeRef(item.sequentialId, item.id),
          value: item.id,
        }))
      : [];
  return { refs, options };
}

/** Mesmo conjunto de ids, sem olhar ordem nem caixa (evita reprocessar a mesma seleção). */
export function sameIdSet(a: string[], b: string[]): boolean {
  const left = new Set(a.map(id => id.toLowerCase()));
  const right = new Set(b.map(id => id.toLowerCase()));
  return left.size === right.size && [...left].every(id => right.has(id));
}

/** Parcelas vinculadas a um ou vários derivativos (união, sem repetidos). */
export function loadLinkedInstallmentIds(
  derivativesHttpService: DerivativesHttpService,
  derivativeIds: string[]
): Observable<string[]> {
  return forkJoin(derivativeIds.map(id => derivativesHttpService.getById(id))).pipe(
    map(derivatives => [
      ...new Set(
        derivatives
          .flatMap(d =>
            (d?.derivativeOperationInstallments ?? []).map(i => i.loanOperationInstallmentId)
          )
          .filter(Boolean)
      ),
    ])
  );
}

/** Rótulo da lista inteira, para o campo travado, a revisão e o título automático. */
export function formatDerivativeRefs(derivatives: CommitteeDerivativeRef[]): string {
  return derivatives.map(item => formatDerivativeRef(item.sequentialId, item.id)).join(', ');
}

/** Um derivativo na descrição do comitê (card 3278). Campo nulo ou zerado sai da linha. */
export interface CommitteeDerivativeDescriptionRow {
  sequentialId: string;
  strategyLabel?: string | null;
  installmentNumbers?: number[];
  currency?: string | null;
  notional?: number | null;
  dueAt?: string | null;
  forward?: number | null;
  cap?: number | null;
  strikeBuy?: number | null;
  strikeSell?: number | null;
  premium?: number | null;
  bankName?: string | null;
}

/**
 * Descrição do comitê com derivativos, no formato do Painel de Risco (`buildSimulationDescription`):
 * cabeçalho, uma linha por derivativo e o fecho. Usada pela tela de Derivativos e pelo campo
 * Derivativo da própria modal (card 3278).
 */
export function buildCommitteeDerivativesDescription(
  header: string,
  rows: CommitteeDerivativeDescriptionRow[],
  footer = 'Números conforme cadastrados na tela de Derivativos.'
): string {
  if (rows.length === 0) return '';
  const lines = rows.map(row => {
    const installments = [...(row.installmentNumbers ?? [])].sort((a, b) => a - b);
    const parts = [
      [row.sequentialId, row.strategyLabel].filter(Boolean).join(' — '),
      installments.length
        ? `${installments.length > 1 ? 'Parcelas' : 'Parcela'} ${installments.map(n => `#${n}`).join(', ')}`
        : null,
      isPositive(row.notional)
        ? `Notional ${[row.currency, formatAmount(row.notional!)].filter(Boolean).join(' ')}`
        : null,
      row.dueAt ? `Venc. ${formatIsoDate(row.dueAt)}` : null,
      isPositive(row.forward) ? `Forward ${formatRate(row.forward!)}` : null,
      isPositive(row.cap) ? `CAP ${formatRate(row.cap!)}` : null,
      isPositive(row.strikeBuy) ? `Strike compra ${formatRate(row.strikeBuy!)}` : null,
      isPositive(row.strikeSell) ? `Strike venda ${formatRate(row.strikeSell!)}` : null,
      isPositive(row.premium) ? `Prêmio ${formatAmount(row.premium!)}` : null,
      row.bankName ? `Banco ${row.bankName}` : null,
    ];
    return parts.filter(Boolean).join(' | ');
  });
  return [header, ...lines, footer].join('\n');
}

function isPositive(value: number | null | undefined): boolean {
  return value != null && Number.isFinite(value) && value > 0;
}

/** Valores com 2 casas no padrão pt-BR (mesmo do Painel). */
function formatAmount(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Taxas com 4 casas, padrão das telas de derivativos. */
function formatRate(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

/** `yyyy-MM-dd` (ou ISO com hora) para `dd/MM/yyyy` por string, sem `new Date()` (régua de datas). */
function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}
